<script>
import BaseView from "@/mixins/BaseView.vue";
import PhoneNumber from "@/components/phones/PhoneNumber.vue";
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import TruckStatus from "@/components/statuses/TruckStatus.vue";
import UserStatus from "@/components/statuses/UserStatus.vue";
import TruckEquipmentBlock from "@/views/fleets/components/EquipmentBlock.vue";
import TruckContactBlock from "@/views/fleets/components/ContactBlock.vue";
import TruckNotes from "@/views/fleets/components/TruckNotes.vue";
import ChangeTruckStatusAction from "@/views/fleets/components/ChangeTruckStatusAction.vue";
import ReserveTruckAction from "@/views/fleets/components/ReserveTruckAction.vue";
import TrucksMixin from "@/views/fleets/mixins/TrucksMixin.vue";
import GoogleMapsMixin from "@/mixins/google-maps.mixin.ts";
import PopupMixin from "@/mixins/popup-mixin.ts";
import OmniTable from "@/components/omni/OmniTable.vue";
import ExpandButton from "@/components/buttons/ExpandButton.vue";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import { mapActions, mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
// @ts-ignore
import TruckMember from "@/components/truck/TruckMember.vue";
import { useDateTime } from "@/composables/index.ts";
import TruckContactPersonRoles from "@/views/fleets/components/TruckContactPersonRoles.vue";
import { useTruckStore } from "@/store/truck.store.ts";
import { TruckStatus as TruckStatusEnum } from "@/constants/truck.ts";
import { DriverStatus, DriverStatusOnHold, OwnerStatus } from "@/constants/user.ts";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";

const { dateTZ } = useDateTime();

export default {
  name: "TrucksList",
  components: {
    PermissionTooltip,
    OmniTooltip,
    TruckContactPersonRoles,
    TruckMember,
    ReserveTruckAction,
    ChangeTruckStatusAction,
    TruckLocationInfo,
    ExpandButton,
    OmniTable,
    UserStatus,
    TruckStatus,
    TruckNotes,
    TruckContactBlock,
    TruckEquipmentBlock,
    PhoneNumber,
  },
  mixins: [BaseView, TrucksMixin, UsersMixin, PopupMixin, GoogleMapsMixin],
  props: {
    availableColumns: { type: Array, default: () => [] },
    searchParams: Object,
    compact: Boolean,
  },
  emits: ["params", "handle-error"],
  data() {
    return {
      data: { items: [], total: 0 },
      sortBy: [{ key: "distance", order: "asc" }],
      sortDesc: false,
      truck: {},
      expanded: [],
      loading: false,
      archived: false,
    };
  },
  computed: {
    DispatcherPermissionValue() {
      return DispatcherPermissionValue;
    },
    ...mapState(useAppStore, ["dispatcher", "dispatchers"]),
    ...mapState(useTruckStore, ["truckReservedWS"]),
    isTrucksSearch() {
      return this.$route.name === "trucks-search";
    },
    hasAccessToReserve() {
      return this.hasAccess(DispatcherPermissionValue.ReserveTruck);
    },
    hasAccessToView() {
      return this.hasAccess(DispatcherPermissionValue.ViewTruckInfo);
    },
    headers() {
      const allHeaders = {
        name: { key: "name", title: this.$t("general.tt"), sortable: false, class: "col-name" },
        status: {
          key: "status",
          title: this.$t("general.status"),
          sortable: false,
          align: "center",
          class: "col-status",
        },
        driver: {
          key: "driver",
          title: this.$t("general.driver-info"),
          sortable: false,
        },
        size: {
          key: "size",
          title: this.$t("trucks.dims-payload"),
          sortable: false,
          align: "right",
          class: "col-dims",
        },
        available: {
          key: "available",
          title: this.$t("trucks.available-location"),
          sortable: false,
          class: "col-location",
        },
        current: {
          key: "current",
          title: this.$t("trucks.current-location"),
          sortable: false,
          class: "col-location",
        },
        contact: {
          key: "contact",
          title: this.$t("trucks.contact-person"),
          sortable: false,
        },
        distance: {
          key: "distance",
          title: this.$t("trucks.distance"),
          sortable: false,
          class: "col-distance",
        },
        notes: {
          key: "notes",
          title: this.$t("trucks.notes"),
          sortable: false,
          class: "notes",
        },
      };

      const headers = this.availableColumns.map((name) => allHeaders[name]);
      headers.push({
        title: "Actions",
        key: "actions",
        align: "left",
        class: "col-actions",
        sortable: false,
      });

      return headers;
    },
  },
  watch: {
    async searchParams() {
      this.params.page = 1;
      await this.refresh();
    },
    archived() {
      this.refresh();
    },
    truckReservedWS(value) {
      this.handleReservedTruckWS(value);
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    ...mapActions(useTruckStore, ["setTruckReservedWS"]),
    async init() {
      if (!this.searchParams) {
        return;
      }

      this.params.page = 1;

      if (!this.isTrucksSearch) {
        await this.refresh();
      }

      if (this.searchParams.location) {
        await this.refresh();
      }
    },
    async expand(itemId) {
      if (this.expanded.length === 1 && this.expanded[0] === itemId) {
        this.expanded = [];
      } else {
        this.expanded = [itemId];

        const truck = this.data.items.find((t) => t.id === itemId);
        await this.loadExtraData(truck);
      }
    },
    async loadExtraData(truck) {
      // load truck owner coordinators
      if (truck.coordinators === undefined) {
        const resp = await this.$api.users.findOwnerCoordinators(truck.ownerId);
        if (resp.success) {
          truck.coordinators = resp.data;
        }
      }

      // load driver data for truck owner only once
      if (truck.owner && truck.owner.isDriver && truck.owner.licenseNumber === undefined) {
        const resp = await this.$api.users.findDriverById(truck.owner.id);
        if (resp.success) {
          truck.owner = { ...truck.owner, ...resp.data };
        }
      }

      // load driver data for truck driver 1 only once
      if (truck.driverId && truck.driver.licenseNumber === undefined) {
        const resp = await this.$api.users.findDriverById(truck.driverId);
        if (resp.success) {
          truck.driver = { ...truck.driver, ...resp.data };
        }
      }

      // load driver data for truck driver 2 only once
      if (truck.driver2Id && truck.driver2.licenseNumber === undefined) {
        const resp = await this.$api.users.findDriverById(truck.driver2Id);
        if (resp.success) {
          truck.driver2 = { ...truck.driver2, ...resp.data };
        }
      }
    },
    sortTrucks() {
      const sortingField = this.searchParams.byHomeAddress ? "homeDistance" : "pickupDistance";

      this.data.items.sort((a, b) => {
        if (a[sortingField] < b[sortingField]) return -1;
        if (a[sortingField] > b[sortingField]) return 1;
        return 0;
      });
    },
    async refresh() {
      this.loading = true;
      const params = { ...this.searchParams, ...this.params };
      params.archived = this.archived;

      if (this.isTrucksSearch) {
        params.pageSize = -1;
      }

      const resp = await this.$api.trucks.findTrucks(params);

      if (resp.success) {
        this.data = resp.data;
        this.$emit("params", resp.data.params);
        this.sortTrucks();
      }

      if (resp.errorData?.detail) {
        this.$emit("handle-error", resp.errorData.detail);
        this.data = [];
      } else {
        this.$emit("handle-error", null);
      }

      this.loading = false;
    },
    updateTruck(truck) {
      const currentTruck = this.data.items.find((t) => t.id === truck.id);
      if (!currentTruck) return;

      const { pickupDistance, ...rest } = truck;

      Object.assign(currentTruck, rest);

      if (currentTruck.driver && truck.driver) {
        currentTruck.driver.note = truck.driver.note;
        currentTruck.driver.driverNoteText = truck.driver.driverNoteText;
      }

      if (currentTruck.driver2 && truck.driver2) {
        currentTruck.driver2.note = truck.driver2.note;
        currentTruck.driver2.driverNoteText = truck.driver2.driverNoteText;
      }
    },
    handleReservedTruckWS(reservedTruckWS) {
      if (!reservedTruckWS) {
        return;
      }

      this.data.items = this.data.items.map((truck) => {
        if (truck.id === reservedTruckWS.truckId) {
          return {
            ...truck,
            reservedById: reservedTruckWS.reservedById,
            reservedTill: reservedTruckWS.reservedTill,
          };
        }

        return truck;
      });

      this.setTruckReservedWS(null);
    },
    async openMapSettingsWindow() {
      if ("getScreenDetails" in window && "isExtended" in screen && screen.isExtended) {
        await window.getScreenDetails();
      }
      const routeData = this.$router.resolve({ name: "map-position" });
      this.openPopup({
        url: routeData.href,
        title: "PopupSettings",
        w: this.popupWidth(),
        h: this.popupHeight(),
        x: this.popupX(),
        y: this.popupY(),
      });
    },
    openRouteMap(from, to) {
      const href = this.getRouteUrl(from, to);
      this.openPopup({
        url: href,
        title: "RouteMap",
        w: this.popupWidth(),
        h: this.popupHeight(),
        x: this.popupX(),
        y: this.popupY(),
      });
    },
    openPlaceMap(place) {
      const href = this.getPlaceUrl(place, 6);
      this.openPopup({
        url: href,
        title: "PlaceMap",
        w: this.popupWidth(),
        h: this.popupHeight(),
        x: this.popupX(),
        y: this.popupY(),
      });
    },
    dateToTimezone(date, timezone) {
      return dateTZ(date, timezone);
    },
    viewDistanceRoute(item) {
      if (this.searchParams.byHomeAddress && item.driver.addressLocation) {
        this.openRouteMap(item.driver.addressLocation, this.data.pickupLocation);
      } else if (item.availableLocation?.coordinates) {
        this.openRouteMap(item.availableLocation.coordinates, this.data.pickupLocation);
      }
    },
    getCellClass(cell) {
      if (cell.column.key !== "name") {
        return "";
      }

      const onHoldChecks = [
        () => cell.item.status === TruckStatusEnum.OnHold,
        () => cell.item.owner.driverStatus === DriverStatus.OnHold,
        () => cell.item.owner.ownerStatus === OwnerStatus.OnHold,
        () => DriverStatusOnHold.includes(cell.item.driver?.driverStatus),
        () => DriverStatusOnHold.includes(cell.item.driver2?.driverStatus),
      ];

      const hasOnHold = onHoldChecks.some((check) => check());

      return hasOnHold ? "border-none border-left border-left-danger border-width-3" : "";
    },
    getCellProps(cell) {
      return {
        class: this.getCellClass(cell),
      };
    },
    hasReserved(item) {
      return item.reservedById && this.getReservationTime(item) > -1;
    },
  },
};
</script>

<template>
  <div v-if="data.pickupAddress" class="text-body-2 mb-2 d-flex align-center">
    {{ data.pickupAddress }}
    <div v-if="data.pickupDeliveryDistance > 0">
      &rarr; {{ data.deliveryAddress }} &bsim; {{ $filters.miles(data.pickupDeliveryDistance) }} miles
      <a
        class="text-decoration-none text-primary ml-2 cursor-pointer text-uppercase font-weight-500 font-size-12"
        target="_blank"
        @click="openRouteMap(data.pickupLocation, data.deliveryLocation)"
      >
        <v-icon color="primary" size="small">mdi-open-in-new</v-icon>
        <span> View on route</span>
      </a>
    </div>

    <a
      v-else
      class="d-flex align-center text-decoration-none text-primary ml-2 cursor-pointer text-uppercase font-weight-500 font-size-12"
      target="_blank"
      @click="openPlaceMap(data.pickupLocation)"
    >
      <v-icon class="mr-1" color="primary" size="small">mdi-open-in-new</v-icon>
      <span> View on map</span>
    </a>

    <div class="d-flex justify-space-between align-center ml-3 font-size-12">
      <div class="cursor-pointer" @click="openMapSettingsWindow">
        <v-icon color="primary" size="small">mdi-cog</v-icon>
      </div>
    </div>
  </div>

  <omni-table
    v-model:archived="archived"
    v-model:expanded="expanded"
    v-model:sort-by="sortBy"
    :cell-props="getCellProps"
    :compact="compact"
    :footer-props="{ 'items-per-page-options': isTrucksSearch ? [] : pagination.itemsPerPage }"
    :headers="headers"
    :hide-default-footer="isTrucksSearch"
    :items="data.items"
    :items-length="data.total"
    :items-per-page="params.pageSize"
    :loading="loading"
    :mobile-breakpoint="0"
    show-archive
    @update:page="updatePage"
    @update:items-per-page="updatePageSize"
  >
    <template #[`header.distance`]>
      <div class="d-flex align-center justify-space-between">
        <span>Distance</span>

        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon color="primary" size="small" v-bind="tooltipProps">mdi-information-outline</v-icon>
          </template>

          Distance from the available location to the city <br />
          Distance from the home location to the city
        </v-tooltip>
      </div>
    </template>

    <template #[`header.status`]>
      <div class="d-flex align-center justify-space-between">
        <span>Status</span>

        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon size="small" v-bind="tooltipProps">mdi-information-outline</v-icon>
          </template>

          <span class="font-weight-500">{{ $t("trucks.tooltip-status-info-title") }}</span>
          <br />
          <template v-for="(point, index) in $tm('trucks.tooltip-status-info-points')" :key="index">
            • {{ point }} <br />
          </template>

          <div class="font-weight-500 mt-2">{{ $t("trucks.tooltip-status-bottom-title") }}</div>
        </v-tooltip>
      </div>
    </template>

    <template #[`item.name`]="{ item }">
      <div class="d-flex align-center">
        <expand-button
          :expanded="expanded.length === 1 && expanded[0] === item.id"
          class="mr-3"
          color="primary"
          size="xsmall"
          @click="expand(item.id)"
        />
        <div>
          <router-link
            :to="{ name: 'truck-view', params: { id: item.id } }"
            class="text-decoration-none text-primary mb-1"
            data-qa="truck-number"
          >
            {{ item.number }}
          </router-link>
          <div class="text-grey-darken-2 font-size-12" data-qa="truck-type">{{ getTruckType(item.type) }}</div>
        </div>
      </div>
    </template>
    <template #[`item.status`]="{ item }">
      <div class="col-status" data-qa="truck-status">
        <truck-notes
          v-if="
            item.note ||
            (item.driver && (item.driver.driverNoteText || item.driver.note)) ||
            (item.driver2 && (item.driver2.driverNoteText || item.driver2.note))
          "
          :truck="item"
        />
        <change-truck-status-action :truck="item" @success="updateTruck">
          <truck-status :truck="item" class="pointer" />
        </change-truck-status-action>
      </div>
    </template>
    <template #[`item.driver`]="{ item }">
      <div v-if="item.driver">
        <truck-member :member="item.driver" driver-name-class="mb-2" />
      </div>
    </template>
    <template #[`item.distance`]="{ item }">
      <div class="d-flex justify-space-between">
        <div :class="`${searchParams.byHomeAddress ? 'flex-column-reverse' : 'flex-column'}`" class="d-flex">
          <div class="d-flex text-no-wrap align-center distance-block">
            <v-tooltip>
              <template #activator="{ props: tooltipProps }">
                <div
                  :class="{ 'font-weight-bold': !searchParams.byHomeAddress }"
                  class="d-flex align-center"
                  v-bind="tooltipProps"
                >
                  <v-icon :color="!searchParams.byHomeAddress ? 'black' : 'grey-darken-2'" class="mr-1" size="small">
                    mdi-cube-outline
                  </v-icon>
                  <span class="font-size-12 text-grey-darken-3 mr-2" data-qa="distance-value">
                    {{ $filters.miles(item.pickupDistance, "round") }} miles
                  </span>
                </div>
              </template>

              Distance from the available location to the city
            </v-tooltip>
          </div>
          <div
            v-if="item.homeDistance !== null && item.homeDistance !== undefined"
            class="d-flex btext-no-wrap align-center distance-block"
          >
            <v-tooltip>
              <template #activator="{ props: tooltipProps }">
                <div
                  :class="{ 'font-weight-bold': searchParams.byHomeAddress }"
                  class="d-flex align-center"
                  v-bind="tooltipProps"
                >
                  <v-icon :color="searchParams.byHomeAddress ? 'black' : 'grey-darken-2'" class="mr-1" size="small"
                    >mdi-home-outline
                  </v-icon>
                  <span class="font-size-12 text-grey-darken-3 mr-2"
                    >{{ $filters.miles(item.homeDistance, "round") }} miles</span
                  >
                  <a
                    v-if="item.deliveryLocation && item.driver && item.driver.addressLocation"
                    :class="item.id"
                    class="text-decoration-none text-primary font-size-11 cursor-pointer"
                    target="_blank"
                    @click="openRouteMap(item.deliveryLocation, item.driver.addressLocation)"
                  >
                    View route
                  </a>
                </div>
              </template>

              Distance from the home location to the city
            </v-tooltip>
          </div>
        </div>

        <a
          v-if="data.pickupLocation"
          class="text-decoration-none text-primary cursor-pointer font-size-11"
          target="_blank"
          @click="viewDistanceRoute(item)"
        >
          View route
        </a>
      </div>
    </template>
    <template #[`item.contact`]="{ item }">
      <div v-if="item.driver && item.driver.contactPerson">
        <div class="d-flex align-center mb-2">
          <span class="currency-icon mr-2 small" />
          <truck-contact-person-roles
            v-if="item.driver.contactPerson"
            :contact-person="item.driver.contactPerson"
            data-qa="contact-person-role"
          />
          <router-link
            :to="{ name: 'user-view', params: { id: item.driver.contactPersonId } }"
            class="text-primary text-decoration-none mr-2 line-height-1"
            data-qa="contact-person-name"
          >
            {{ item.driver.contactPerson.name }}
          </router-link>
          <user-status :status="item.driver.driverStatus" data-qa="contact-person-status" />
        </div>

        <div class="d-flex align-center">
          <phone-number
            :number="item.driver.contactPerson.phone"
            class="font-size-12 text-decoration-none text-primary"
          />
          <span
            class="ml-2 text-uppercase bg-grey-lighten-4 rounded text-grey-darken-1 pr-1 pl-1 caption type-language"
          >
            {{ $filters.upper(item.driver.language) }}
          </span>
        </div>
      </div>
    </template>
    <template #[`item.notes`]="{ item }">
      <truck-notes :truck="item" @update="updateTruck" />
      <div v-if="item.driver" class="text-grey-darken-3">
        {{ $filters.mdash(item.driver.driverNote) }}
      </div>
      <div v-if="item.note" class="text-grey-darken-3">
        {{ item.note }}
      </div>
    </template>
    <template #[`item.size`]="{ item }">
      <template v-if="item.trailer">
        <div class="text-grey-darken-3" data-qa="truck-trailer-dims">
          {{ item.trailer.length }}&#8243;&nbsp;х&nbsp;{{ item.trailer.minWidth }}&#8243;&nbsp;x&nbsp;{{
            item.trailer.minHeight
          }}&#8243;
        </div>
        <div class="text-grey-darken-1 font-size-12">{{ item.trailer.payload }} lbs</div>
      </template>
      <template v-else> &mdash;</template>
    </template>
    <template #[`item.available`]="{ item }">
      <truck-location-info
        v-if="item.availableLocation"
        :current-location="item.currentLocation"
        :current-location-time="item.currentLocationTime"
        :location="item.availableLocation"
        :show-info-menu="isTrucksSearch"
        :show-tooltip="false"
        :time="item.availableDate"
        hide-dot
        show-changed-by
      />
    </template>
    <template #[`item.current`]="{ item }">
      <truck-location-info :location="item.currentLocation" :time="item.currentLocationTime" show-indicator />
    </template>
    <template #[`item.actions`]="{ item }">
      <div class="d-flex align-center">
        <permission-tooltip :permission="DispatcherPermissionValue.EditOrCreateTruck">
          <template #activator="{ disabled }">
            <v-btn
              :disabled="disabled"
              :to="{ name: 'truck-edit', params: { id: item.id } }"
              class="pa-1 table-action mr-2"
              flat
              size="small"
              variant="text"
            >
              <v-icon class="text-primary">mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
          {{ $t("permission.edit-truck") }}
        </permission-tooltip>

        <omni-tooltip :disabled="hasAccessToView">
          <template #activator>
            <v-btn
              :disabled="!hasAccessToView"
              :to="{ name: 'truck-view', params: { id: item.id } }"
              class="pa-1 table-action mr-2"
              flat
              size="small"
              variant="text"
            >
              <v-icon class="text-primary">mdi-eye-outline</v-icon>
            </v-btn>
          </template>

          {{ $t("permission.view-truck") }}
        </omni-tooltip>

        <omni-tooltip v-if="hasReserved(item)" top>
          <template #activator>
            <reserve-truck-action :disabled="item.reservedById !== dispatcher.id" :truck="item" @success="updateTruck">
              <v-btn
                :disabled="item.reservedById !== dispatcher.id"
                class="font-weight-600"
                color="success"
                size="small"
                variant="flat"
              >
                RESERVED, {{ getReservationTime(item) }}m
              </v-btn>
            </reserve-truck-action>
          </template>
          <span>
            Reserved by {{ getReservedBy(item) }} until
            {{ dateToTimezone(item.reservedTill, dispatcher.timezone) }}
          </span>
        </omni-tooltip>

        <omni-tooltip v-else :disabled="hasAccessToReserve">
          <template #activator>
            <reserve-truck-action :disabled="!hasAccessToReserve" :truck="item" @success="updateTruck">
              <v-btn
                :disabled="!hasAccessToReserve"
                class="text-uppercase font-weight-600"
                color="primary"
                size="small"
                variant="flat"
                >Reserve</v-btn
              >
            </reserve-truck-action>
          </template>

          {{ $t("permission.reserve-truck") }}
        </omni-tooltip>
      </div>
    </template>
    <template #expanded-row="{ columns, item }">
      <tr>
        <td :colspan="columns.length" class="dop-row">
          <div class="bg-grey-lighten-4 pt-3 pr-4 pb-3 pl-4 dop-row__inner">
            <div class="blocks-items d-flex flex-wrap">
              <truck-equipment-block :truck="item" />
              <truck-contact-block
                v-if="item.owner && ![item.driverId, item.driver2Id].includes(item.ownerId)"
                :is-contact-person="
                  item.driver && item.driver.contactPersonId && item.ownerId === item.driver.contactPersonId
                "
                :label="$t('drivers.owner')"
                :user="item.owner"
                owner
                show-certificates
              />
              <truck-contact-block
                v-if="item.driver"
                :is-contact-person="item.driver.contactPersonId && item.driverId === item.driver.contactPersonId"
                :label="item.driver.id === item.owner.id ? $t('drivers.owner-driver') : $t('drivers.driver')"
                :user="item.driver"
                show-certificates
              />
              <truck-contact-block
                v-if="item.driver2"
                :is-contact-person="item.driver2Id === item.driver.contactPersonId"
                :label="item.driver2.id === item.owner.id ? $t('drivers.owner-driver') : $t('drivers.second-driver')"
                :user="item.driver2"
                show-certificates
              />

              <truck-contact-block
                v-for="coordinator in item.coordinators"
                :key="coordinator.userId"
                :full-access="coordinator.fullAccess"
                :is-contact-person="coordinator.user.id === item.driver.contactPersonId"
                :show-status="false"
                :user="coordinator.user"
                label="Coordinator"
              />
            </div>
          </div>
        </td>
      </tr>
    </template>
  </omni-table>
</template>

<style lang="scss" scoped>
.dop-row {
  border-right: 0 !important;
  padding: 0 0 12px 0 !important;
  background: #f8f8fb;

  &__inner {
    box-shadow: 0 4px 4px -1px rgba(0, 0, 0, 0.16);
  }
}

.blocks-items {
  margin: 0 -8px;
}

.table-action {
  width: auto !important;
  min-width: auto !important;
  padding: 0 2px !important;
  background: transparent !important;

  &:not(:last-child) {
    margin-right: 10px;
  }
}

.location {
  line-height: 17px;
}

.table-status {
  white-space: nowrap;
  display: inline-block;
  line-height: 16px !important;
}

.type-language {
  line-height: 12px;
  font-size: 11px;
  padding: 2px 0;
}

.line-height-1 {
  line-height: 1 !important;
}

.status-item {
  font-size: 9px;
  line-height: 16px;
  height: 16px !important;
  border-radius: 100px;
  padding: 0 2px !important;
  min-width: auto !important;
}

.currency-icon {
  width: 18px;
  flex: 0 0 18px;
  height: 18px;
  font-size: 13px;
  display: inline-block;
  text-align: center;
  border-radius: 50%;
  background: url(@/assets/images/currency-icon.svg) no-repeat;
  background-size: 18px;

  &.small {
    width: 14px;
    flex: 0 0 14px;
    height: 14px;
    background-size: 14px;
  }
}
</style>

<style lang="scss" scoped>
.col-name {
  min-width: 160px;
  width: 10%;
}

thead .col-status {
  width: 6%;
  min-width: 145px;
}

.col-info {
  width: 15%;
  min-width: 250px;
}

.col-dims,
.col-payload {
  width: 1%;
  min-width: 150px;
}

.col-location {
  width: 15%;
  min-width: 165px;
}

.col-actions {
  width: 1%;
  min-width: 170px;
}

.col-distance {
  width: 11.8%;
  min-width: 180px;
}

.v-application .location-block .caption {
  font-size: 11px !important;
  line-height: 12px;
}

.font-size-11 {
  font-size: 11px;
}
</style>
